import { useState } from 'react';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatVND } from '@/lib/utils';
import {
  confirmPlannedExpense, confirmPlannedIncome, cancelPlanned, addPlanned, updatePlanned,
  addPlannedPayment, updatePlannedPayment, cancelPlannedPayment, saveTemplate, type TemplateInput
} from '../../../ledger/plans';
import { confirmPlannedCreditPayment } from '../../../services/plannedCreditPaymentService';
import { useData } from '../../store/data';
import { FormSheet, Field, MoneyField, WalletSelect } from '../../components/FormSheet';
import { parseAmount } from '../entry/formSchema';
import { variance, type PlannedEntry } from './plannedEntries';
import { paymentCap, cappedPayment, fundingIssue } from './paymentCap';
import { splitDebtPayment, followsLast } from '../../../finance';
import { parseMoneyOrNull } from '../entry/formSchema';

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const uid = () => getAuth().currentUser!.uid;
const nextMonthKey = (isoDate: string) => {
  const [y, m] = isoDate.split('-').map(Number);
  const d = new Date(y, m, 1); // m là tháng 1-based → new Date(y, m, 1) = tháng kế tiếp
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const CANCEL_REASONS = ['Không còn phát sinh', 'Đã chi/thu bằng cách khác', 'Nhập nhầm', 'Đóng sổ khoản quá hạn'];

// ───────── Xác nhận (+ P5: gợi ý cập nhật mẫu khi lệch số) ─────────
export function ConfirmSheet({ entry, onClose }: { entry: PlannedEntry; onClose: () => void }) {
  const d = entry.doc;
  const { sources, recurringTemplates, debts } = useData(s => s.data);
  const isPay = entry.kind === 'payment';
  const wallets = sources.filter(s => s.status !== 'inactive' && (entry.kind === 'expense' ? true : s.type !== 'credit'));
  // Khoản trả thẻ/nợ không được vượt số còn nợ thật: điền sẵn số nhỏ hơn giữa mẫu và dư nợ
  const cap = isPay ? paymentCap(d, sources, debts) : null;
  const suggested = isPay ? cappedPayment(entry.amount, cap) : entry.amount;
  const [amount, setAmount] = useState(String(suggested || entry.amount || ''));
  const [walletId, setWalletId] = useState<string>((isPay ? d.sourceWalletId : d.walletId) ?? '');
  const [date, setDate] = useState(iso(new Date()));
  const [updateTpl, setUpdateTpl] = useState(true);
  const [interest, setInterest] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tpl = entry.templateId ? recurringTemplates.find((t: any) => t.id === entry.templateId) : undefined;
  const actual = parseAmount(amount);
  const v = variance(entry.amount, actual);
  // Số thấp hơn mẫu chỉ vì khoản nợ sắp hết (đã điền sẵn theo dư nợ) thì không gợi ý hạ mẫu
  const cappedByDebt = isPay && cap !== null && entry.amount > cap && actual === suggested;
  const offerTemplate = !!tpl && actual > 0 && v.significant && !cappedByDebt && !followsLast(tpl);
  // Trả nợ vay: tách gốc/lãi. Để trống ô lãi = tính theo công thức; nhập số = lãi thực tế theo sao kê
  const debt = isPay && d.paymentType === 'debt' ? debts.find((x: any) => x.id === d.debtId) : undefined;
  const typedInterest = interest.trim() ? parseMoneyOrNull(interest) : null;
  const split = debt && actual > 0 ? splitDebtPayment(Number(debt.remainingBalance) || 0, Number(debt.interestRate) || 0, actual, typedInterest) : null;

  // Khoản chi/trả: nguồn tiền phải đủ (thẻ còn hạn mức, ví ngân hàng không âm) — báo ngay khi chọn, không đợi bấm
  const funding = entry.kind === 'income' ? null : fundingIssue(sources.find(x => x.id === walletId), actual);

  const submit = async () => {
    if (actual <= 0) { setError('Nhập số tiền lớn hơn 0'); return; }
    if (!walletId) { setError('Chọn ví'); return; }
    if (funding) { setError(funding); return; }
    if (isPay && d.paymentType === 'credit' && cap !== null && actual > cap) { setError(cap === 0 ? `Thẻ ${d.creditCardName ?? ''} hiện không còn dư nợ — không có gì để trả. Hãy bỏ qua khoản này.` : `Số trả vượt dư nợ thẻ (${formatVND(cap)}). Giảm số trả xuống tối đa ${formatVND(cap)}.`); return; }
    if (interest.trim() && typedInterest === null) { setError('Tiền lãi không hợp lệ'); return; }
    if (split && split.excess > 0) { setError(`Gốc trả (${formatVND(split.principal)}) vượt dư nợ còn lại ${formatVND(Number(debt!.remainingBalance) || 0)}`); return; }
    setBusy(true); setError(null);
    const w = sources.find(x => x.id === walletId);
    let r: any;
    if (isPay) r = await confirmPlannedCreditPayment(uid(), entry.id, { amount: actual, sourceWalletId: walletId, date, interest: typedInterest });
    else r = await (entry.kind === 'income' ? confirmPlannedIncome : confirmPlannedExpense)(uid(), entry.id, { amount: actual, walletId, walletName: w?.name, transactionDate: date });
    if (!r?.success) { setBusy(false); setError(r?.error ?? 'Không xác nhận được'); return; }
    r.warnings?.forEach((x: string) => toast.warning(x, { duration: 7000 }));
    let tplMsg = '';
    if (offerTemplate && updateTpl && tpl) {
      const t = await saveTemplate(uid(), tpl.id, { ...(tpl as any), amount: actual } as TemplateInput);
      tplMsg = t.success ? ' Mẫu định kỳ đã cập nhật.' : ' (Chưa cập nhật được mẫu.)';
    }
    setBusy(false);
    toast.success(`${isPay ? 'Đã ghi khoản trả' : entry.kind === 'income' ? 'Đã ghi khoản thu' : 'Đã ghi khoản chi'}.${tplMsg}`); onClose();
  };

  return (
    <FormSheet open onClose={onClose} title={`Xác nhận: ${entry.title}`} onSubmit={submit} submitLabel="Xác nhận & ghi sổ" busy={busy} error={error} disabled={!!funding}
      description={`Dự kiến ${formatVND(entry.amount)}. Sửa số tiền, ví, ngày nếu thực tế khác — khoản sẽ được ghi thành giao dịch chính thức.${isPay ? ' Với khoản nợ, lãi được tách khỏi gốc.' : ''}`}>
      {isPay && cap !== null && entry.amount > cap && (
        <p role="note" className="rounded-md bg-warn-soft px-3 py-2 text-sm text-warn">
          {cap === 0
            ? `${d.paymentType === 'credit' ? 'Thẻ' : 'Khoản vay'} này hiện không còn nợ nên không có gì để trả. Nếu đã tất toán, hãy bỏ qua khoản này và tắt mẫu định kỳ.`
            : `Mẫu ghi ${formatVND(entry.amount)} nhưng ${d.paymentType === 'credit' ? 'thẻ chỉ còn nợ' : 'khoản vay chỉ còn phải trả tối đa'} ${formatVND(cap)} — đã điền sẵn số này.`}
        </p>
      )}
      <MoneyField label="Số tiền thực tế" value={amount} onChange={setAmount}
        hint={actual > 0 && v.diff !== 0 ? `${v.diff > 0 ? 'Cao hơn' : 'Thấp hơn'} dự kiến ${formatVND(Math.abs(v.diff))} (${Math.round(Math.abs(v.pct) * 100)}%)` : undefined} />
      {debt && (
        <MoneyField label="Trong đó tiền lãi (theo sao kê)" value={interest} onChange={setInterest} allowZero
          hint={split ? `${interest.trim() ? 'Lãi theo sao kê' : 'Lãi tính theo công thức'}: ${formatVND(split.interest)} · Gốc ${formatVND(split.principal)} · Dư nợ sau kỳ này ${formatVND(Math.max(0, (Number(debt.remainingBalance) || 0) - split.principal))}. Để trống = tính theo công thức.` : 'Để trống = tính theo công thức (dư nợ × lãi suất ÷ 12).'} />
      )}
      <WalletSelect label={entry.kind === 'income' ? 'Ví nhận' : isPay ? 'Trả từ ví' : 'Ví / thẻ thanh toán'} value={walletId} onChange={setWalletId} wallets={wallets} />
      {funding && <p role="note" className="rounded-md bg-warn-soft px-3 py-2 text-sm text-warn">{funding}</p>}
      <Field label="Ngày thực tế"><Input type="date" value={date} onChange={e => setDate(e.target.value)} className="num" /></Field>
      {offerTemplate && (
        <label className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm">
          <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={updateTpl} onChange={e => setUpdateTpl(e.target.checked)} />
          <span>Số này lệch từ 10% so với mẫu định kỳ "{tpl!.name}". Cập nhật mẫu thành <b>{formatVND(actual)}</b> cho các tháng sau.</span>
        </label>
      )}
    </FormSheet>
  );
}

// ───────── Sửa khoản đang chờ ─────────
export function EditSheet({ entry, onClose }: { entry: PlannedEntry; onClose: () => void }) {
  const d = entry.doc;
  const { sources, expenseCategories, incomeCategories } = useData(s => s.data);
  const isPay = entry.kind === 'payment';
  const [amount, setAmount] = useState(String(entry.amount));
  const [category, setCategory] = useState<string>(entry.category ?? '');
  const [walletId, setWalletId] = useState<string>((isPay ? d.sourceWalletId : d.walletId) ?? '');
  const [date, setDate] = useState(entry.date ? iso(entry.date) : iso(new Date()));
  const [note, setNote] = useState<string>(entry.detail);
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const cats = [...new Set<string>((entry.kind === 'income' ? incomeCategories : expenseCategories).map((c: any) => c.name))];
  const wallets = sources.filter(s => s.status !== 'inactive' && (entry.kind === 'income' || isPay ? s.type !== 'credit' : true));

  const submit = async () => {
    const e: Record<string, string> = {};
    if (parseAmount(amount) <= 0) e.amount = 'Nhập số tiền lớn hơn 0';
    if (!isPay && !category) e.category = 'Chọn danh mục';
    if (!walletId) e.walletId = 'Chọn ví';
    if (!date) e.date = 'Chọn ngày';
    setErr(e);
    if (Object.keys(e).length) return;
    setBusy(true); setServerError(null);
    const walletName = wallets.find(w => w.id === walletId)?.name;
    const r = isPay
      ? await updatePlannedPayment(uid(), entry.id, { amount: parseAmount(amount), walletId, walletName, date, note })
      : await updatePlanned(uid(), entry.kind as 'expense' | 'income', entry.id, { amount: parseAmount(amount), category, walletId, walletName, date, note });
    setBusy(false);
    if (!r.success) { setServerError(r.error ?? 'Không lưu được'); return; }
    toast.success('Đã cập nhật khoản dự kiến'); onClose();
  };

  return (
    <FormSheet open onClose={onClose} title={`Sửa: ${entry.title}`} onSubmit={submit} submitLabel="Lưu" busy={busy} error={serverError}
      description="Sửa chỉ áp dụng cho khoản này. Muốn đổi cho các tháng sau, sửa ở tab Mẫu định kỳ.">
      <MoneyField label="Số tiền dự kiến" value={amount} onChange={setAmount} error={err.amount} />
      {!isPay && (
        <Field label="Danh mục" error={err.category}>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger aria-invalid={!!err.category}><SelectValue placeholder="Chọn danh mục" /></SelectTrigger>
            <SelectContent>{(category && !cats.includes(category) ? [category, ...cats] : cats).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      )}
      <WalletSelect label={entry.kind === 'income' ? 'Ví nhận' : isPay ? 'Trả từ ví' : 'Ví / thẻ thanh toán'} value={walletId} onChange={setWalletId} wallets={wallets} error={err.walletId} />
      <Field label="Ngày dự kiến" error={err.date}><Input type="date" value={date} onChange={e => setDate(e.target.value)} className="num" /></Field>
      <Field label="Ghi chú"><Input value={note} onChange={e => setNote(e.target.value)} placeholder="Tuỳ chọn" /></Field>
    </FormSheet>
  );
}

// ───────── Bỏ qua (không xoá, giữ lý do) ─────────
export function SkipSheet({ entry, onClose }: { entry: PlannedEntry; onClose: () => void }) {
  const recurringTemplates = useData(s => s.data.recurringTemplates);
  const tpl: any = entry.templateId ? recurringTemplates.find((t: any) => t.id === entry.templateId) : undefined;
  const [reason, setReason] = useState(entry.daysLate > 60 ? CANCEL_REASONS[3] : CANCEL_REASONS[0]);
  const [other, setOther] = useState('');
  // Bỏ qua chỉ huỷ khoản của tháng này; mẫu còn bật thì tháng sau lại sinh. Lý do "không còn phát sinh" thì gợi ý tắt luôn mẫu.
  const [stopTpl, setStopTpl] = useState(!!tpl && reason === CANCEL_REASONS[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true); setError(null);
    const why = other.trim() || reason;
    const r = entry.kind === 'payment' ? await cancelPlannedPayment(uid(), entry.id, why) : await cancelPlanned(uid(), entry.kind, entry.id, why);
    if (!r.success) { setBusy(false); setError(r.error ?? 'Không bỏ qua được'); return; }
    let more = '';
    if (stopTpl && tpl) {
      const t = await saveTemplate(uid(), tpl.id, { ...tpl, active: false } as TemplateInput);
      more = t.success ? ' Mẫu định kỳ đã tắt, tháng sau không sinh nữa.' : ' (Chưa tắt được mẫu — tắt ở Mẫu định kỳ.)';
    }
    setBusy(false);
    toast.success(`Đã bỏ qua — khoản vẫn được lưu kèm lý do.${more}`); onClose();
  };
  return (
    <FormSheet open onClose={onClose} title={`Bỏ qua: ${entry.title}`} onSubmit={submit} submitLabel="Bỏ qua khoản này" busy={busy} error={error}
      description={`${formatVND(entry.amount)} sẽ không được ghi vào sổ. Khoản được giữ lại để còn truy vết.`}>
      <Field label="Lý do">
        <Select value={reason} onValueChange={(v) => { setReason(v); if (tpl) setStopTpl(v === CANCEL_REASONS[0]); }}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{CANCEL_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Lý do khác (nếu có)"><Input value={other} onChange={e => setOther(e.target.value)} placeholder="Ghi rõ nếu không thuộc lựa chọn trên" maxLength={200} /></Field>
      {tpl && (
        <label className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm">
          <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={stopTpl} onChange={e => setStopTpl(e.target.checked)} />
          <span>Tắt mẫu định kỳ "{tpl.name}" từ nay. Không chọn thì chỉ bỏ qua tháng này, tháng sau khoản này vẫn tự xuất hiện.</span>
        </label>
      )}
    </FormSheet>
  );
}

// ───────── Thêm khoản dự kiến (4 loại, có thể lặp hằng tháng) ─────────
type AddKind = 'expense' | 'income' | 'credit' | 'debt';
export function AddSheet({ onClose, defaultDate }: { onClose: () => void; defaultDate?: string }) {
  const { sources, debts, expenseCategories, incomeCategories } = useData(s => s.data);
  const [kind, setKind] = useState<AddKind>('expense');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [target, setTarget] = useState('');
  const [walletId, setWalletId] = useState('');
  const [date, setDate] = useState(defaultDate ?? iso(new Date()));
  const [note, setNote] = useState('');
  const [repeat, setRepeat] = useState(false);
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const isPay = kind === 'credit' || kind === 'debt';
  const cards = sources.filter(s => s.type === 'credit' && s.status !== 'inactive');
  const loans = debts.filter((x: any) => x.type !== 'credit' && x.status !== 'paid');
  const cats = [...new Set<string>((kind === 'income' ? incomeCategories : expenseCategories).map((c: any) => c.name))];
  const wallets = sources.filter(s => s.status !== 'inactive' && (kind === 'income' || isPay ? s.type !== 'credit' : true));
  const targets: Array<[string, string]> = kind === 'credit' ? cards.map(c => [c.id, c.name]) : loans.map((x: any) => [x.id, x.lender]);

  const submit = async () => {
    const e: Record<string, string> = {};
    if (parseAmount(amount) <= 0) e.amount = 'Nhập số tiền lớn hơn 0';
    if (isPay && !target) e.target = kind === 'credit' ? 'Chọn thẻ' : 'Chọn khoản vay';
    if (!isPay && !category) e.category = 'Chọn danh mục';
    if (!walletId) e.walletId = 'Chọn ví';
    if (!date) e.date = 'Chọn ngày';
    setErr(e);
    if (Object.keys(e).length) return;
    setBusy(true); setServerError(null);
    const walletName = wallets.find(w => w.id === walletId)?.name;
    const targetName = targets.find(([id]) => id === target)?.[1];
    const day = Number(date.split('-')[2]);

    let templateId: string | undefined;
    if (repeat) {
      const base = { frequency: 'monthly' as const, dayOfMonth: day, active: true, startMonth: nextMonthKey(date), amount: parseAmount(amount), walletId, walletName };
      const input: TemplateInput = isPay
        ? { ...base, type: 'payment', name: `${kind === 'credit' ? 'Trả thẻ' : 'Trả nợ'} ${targetName ?? ''}`.trim(), category: '', paymentType: kind as 'credit' | 'debt',
            ...(kind === 'credit' ? { creditCardId: target, creditCardName: targetName } : { debtId: target, debtName: targetName }) }
        : { ...base, type: kind as 'expense' | 'income', name: note.trim() || category, category };
      const t = await saveTemplate(uid(), null, input);
      if (!t.success) { setBusy(false); setServerError(t.error ?? 'Không tạo được mẫu định kỳ'); return; }
      templateId = t.id;
    }
    const r = isPay
      ? await addPlannedPayment(uid(), { paymentType: kind as 'credit' | 'debt', targetId: target, targetName, amount: parseAmount(amount), walletId, walletName, date, note, templateId })
      : await addPlanned(uid(), kind as 'expense' | 'income', { amount: parseAmount(amount), category, walletId, walletName, date, note, templateId });
    setBusy(false);
    if (!r.success) { setServerError(repeat ? `Đã tạo mẫu định kỳ nhưng chưa tạo được khoản tháng này: ${r.error}` : r.error ?? 'Không lưu được'); return; }
    toast.success(repeat ? 'Đã thêm khoản dự kiến và mẫu định kỳ (từ tháng sau)' : 'Đã thêm khoản dự kiến'); onClose();
  };

  return (
    <FormSheet open onClose={onClose} title="Thêm khoản dự kiến" onSubmit={submit} submitLabel="Thêm" busy={busy} error={serverError}
      description="Khoản dự kiến chưa trừ hay cộng ví. Chỉ khi bạn xác nhận mới ghi thành giao dịch chính thức.">
      <Tabs value={kind} onValueChange={v => { setKind(v as AddKind); setCategory(''); setTarget(''); setWalletId(''); }}>
        <TabsList className="grid w-full grid-cols-4"><TabsTrigger value="expense">Chi</TabsTrigger><TabsTrigger value="income">Thu</TabsTrigger><TabsTrigger value="credit">Trả thẻ</TabsTrigger><TabsTrigger value="debt">Trả nợ</TabsTrigger></TabsList>
      </Tabs>
      <MoneyField label="Số tiền dự kiến" value={amount} onChange={setAmount} error={err.amount} />
      {isPay ? (
        <Field label={kind === 'credit' ? 'Thẻ tín dụng' : 'Khoản vay'} error={err.target}>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger aria-invalid={!!err.target}><SelectValue placeholder="Chọn" /></SelectTrigger>
            <SelectContent>{targets.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      ) : (
        <Field label="Danh mục" error={err.category}>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger aria-invalid={!!err.category}><SelectValue placeholder="Chọn danh mục" /></SelectTrigger>
            <SelectContent>{cats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      )}
      <WalletSelect label={kind === 'income' ? 'Ví nhận' : isPay ? 'Trả từ ví' : 'Ví / thẻ thanh toán'} value={walletId} onChange={setWalletId} wallets={wallets} error={err.walletId} />
      <Field label="Ngày dự kiến" error={err.date}><Input type="date" value={date} onChange={e => setDate(e.target.value)} className="num" /></Field>
      <Field label="Ghi chú"><Input value={note} onChange={e => setNote(e.target.value)} placeholder="Tuỳ chọn" /></Field>
      <label className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm">
        <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={repeat} onChange={e => setRepeat(e.target.checked)} />
        <span>Lặp lại hằng tháng vào ngày {date ? Number(date.split('-')[2]) : '…'} — tự tạo khoản chờ xác nhận từ tháng sau.</span>
      </label>
    </FormSheet>
  );
}
